import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { tokenStorage } from '../utils/tokenStorage';
import { usePodData } from './usePodData';

/**
 * Custom hook for managing user data
 * @returns {Object} - User data state and methods
 */
export const useUserData = () => {
  const [userData, setUserData] = useState(() => {
    return tokenStorage.getPodSession();
  });
  const [loading, setLoading] = useState(!userData);
  const [error, setError] = useState(null);
  const { podIP } = usePodData();
  const location = useLocation();

  // Watch for session changes in storage
  useEffect(() => {
    const checkSession = () => {
      const session = tokenStorage.getPodSession();
      if (!session) {
        return;
      }

      setUserData(prevUserData => {
        if (!prevUserData || JSON.stringify(session) !== JSON.stringify(prevUserData)) {
          setLoading(false);
          return session;
        }
        return prevUserData;
      });
    };

    // Check immediately - data should be stored after successful sign-in
    checkSession();

    // Listen for custom event when profile is stored (dispatched from AuthCallback)
    const handleProfileStored = () => {
      checkSession();
    };

    // Listen for storage events (for cross-tab scenarios)
    const handleStorageChange = (e) => {
      if (e.key === 'pod_session' || e.key === null) {
        checkSession();
      }
    };

    // Listen for window focus (in case profile was stored while tab was inactive)
    const handleFocus = () => {
      checkSession();
    };

    // Listen for visibility change (when tab becomes visible)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        checkSession();
      }
    };

    window.addEventListener('userProfileStored', handleProfileStored);
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('userProfileStored', handleProfileStored);
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []); // Empty dependency array - set up listeners once and keep them

  // Check profile when route changes (e.g., after navigation from callback)
  useEffect(() => {
    // Skip check on callback route
    if (location.pathname === '/auth/callback') {
      return;
    }

    // Check profile when navigating away from callback or on any route change
    const session = tokenStorage.getPodSession();
    if (session) {
      setUserData(prevUserData => {
        if (!prevUserData || JSON.stringify(session) !== JSON.stringify(prevUserData)) {
          setLoading(false);
          return session;
        }
        return prevUserData;
      });
    }
  }, [location.pathname]);

  useEffect(() => {
    const session = tokenStorage.getPodSession();
    if (session) {
      setUserData(session);
      setLoading(false);
    } else {
      setLoading(false);
    }
  }, [podIP]);

  const updateUserData = (newData) => {
    setUserData(prev => ({ ...prev, ...newData }));
  };

  const refreshUserData = async () => {
    try {
      setLoading(true);
      setError(null);
      const session = tokenStorage.getPodSession();
      if (session) {
        setUserData(session);
      } else {
        throw new Error('No pod session found');
      }
    } catch (err) {
      console.error('Error refreshing user data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return {
    userData,
    loading,
    error,
    updateUserData,
    refreshUserData
  };
};
